import type { DeslindePropuesto, FuenteDeLaPropuesta } from "@/lib/rellenoConIa";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en "Descripción de la propiedad": mezclar lo que el
   modelo propuso con lo que ya hay en el formulario.

   VIVE FUERA DEL COMPONENTE por lo mismo que sus gemelos de aguas y suelos: es
   lo único de la entrega que puede estar mal sin que nada se vea roto. Un
   merge que pise borra trabajo sin un error en consola.

   ══ ACÁ NO HAY UNA LISTA: HAY UN TEXTO POR ROL ═══════════════════════════════

   Suelos tiene una lista (que se AGREGA) y dos párrafos (que se PISAN si están
   vacíos). Acá no hay ninguna lista: cada rol tiene EXACTAMENTE un campo de
   texto, así que la regla de los párrafos de suelos —"solo se escribe si está
   vacío"— es la ÚNICA regla, aplicada rol por rol.

   ══ Y ACÁ SÍ SE PUEDE DESHACER UN TEXTO, AL REVÉS QUE SUELOS ════════════════

   En suelos, "descartar" solo quita FILAS y dejaba los dos párrafos quietos,
   porque deshacer un texto era ambiguo: ¿vuelve a vacío, o a lo que había
   antes de que la persona lo editara? Acá esa ambigüedad no existe, porque
   `sumarLaPropuestaDeDescripcion` NUNCA pisa un campo con texto — solo escribe
   en los que estaban vacíos. Eso significa que el "antes" de cualquier campo
   que la IA llegó a tocar fue SIEMPRE "": no hay nada que la propuesta se
   pueda haber llevado por encima. Por eso `descartarLaPropuestaDeDescripcion`
   sí puede revertir el texto, con una sola condición: que siga siendo
   EXACTAMENTE el que se propuso, porque si la persona ya lo corrigió, ese
   texto es SUYO y no de la IA.
--------------------------------------------------------------------------- */

/** El texto y la casilla de UN rol, mientras se editan. */
export interface DeslindesDelRol {
  texto: string;
  validada: boolean;
}

export type DeslindesPorRol = Record<number, DeslindesDelRol>;

/**
 * Un rol al que se le ESCRIBIÓ el deslinde propuesto.
 *
 * `validadaAntes` es lo que hace posible descartar: si la casilla estaba en
 * `true` de antes —caso raro, pero el tipo del rol lo permite—, revertir el
 * texto tiene que devolver también esa marca, no dejarla en `false` a secas.
 */
export interface EscritoDeDescripcion {
  id: number;
  texto: string;
  validadaAntes: boolean;
  /**
   * De qué documento dice la IA que salió el texto. `null` es "no indicada".
   * Solo se muestra mientras el texto siga igual; no se guarda con los datos.
   */
  fuente: FuenteDeLaPropuesta | null;
}

export interface MezclaDeDescripcion {
  deslindes: DeslindesPorRol;
  escritos: EscritoDeDescripcion[];
}

/**
 * Escribe el deslinde propuesto en cada rol que lo admite, sin pisar nada de
 * lo anterior.
 *
 * ESCRIBE SOLO EN ROLES CON EL CAMPO VACÍO. Un rol con texto se queda tal
 * cual, sea quien sea que lo haya escrito — persona o una corrida anterior de
 * este mismo botón.
 *
 * CUANDO ESCRIBE, FUERZA `validada = false`: un texto que nadie revisó no
 * puede quedar marcado como revisado, aunque la fila llegara con la casilla
 * en `true` de antes. Esa marca de antes se guarda en `validadaAntes`, para
 * que descartar la pueda restaurar.
 *
 * IGNORA lo que no se puede escribir: una propuesta para un `id` que ya no
 * está entre los roles del informe —se borró en Antecedentes generales
 * mientras corría la lectura, y no hay a quién escribirle— y una propuesta
 * con el texto vacío, que no aporta nada.
 */
export function sumarLaPropuestaDeDescripcion(
  actual: DeslindesPorRol,
  propuesta: DeslindePropuesto[],
  rolesIds: number[],
): MezclaDeDescripcion {
  const idsValidos = new Set(rolesIds);
  const escritos: EscritoDeDescripcion[] = [];
  let deslindes = actual;

  for (const fila of propuesta) {
    if (!idsValidos.has(fila.id)) continue;

    // `null` cuando la fila trae solo datos legales.
    const texto = (fila.descripcion_deslindes ?? "").trim();
    if (texto === "") continue;

    const delRol = deslindes[fila.id] ?? { texto: "", validada: false };

    // NUNCA PISA: un rol con texto se queda tal cual está.
    if (delRol.texto.trim() !== "") continue;

    escritos.push({
      id: fila.id,
      texto,
      validadaAntes: delRol.validada,
      fuente: fila.fuente ?? null,
    });
    deslindes = { ...deslindes, [fila.id]: { texto, validada: false } };
  }

  return { deslindes, escritos };
}

/**
 * Revierte SOLO los roles cuyo texto sigue siendo EXACTAMENTE el propuesto.
 *
 * Un rol que la persona corrigió después de que la IA escribió no se toca: lo
 * que hay ahí es SUYO, y descartar la última corrida no puede llevárselo por
 * delante. Ver el encabezado del módulo para por qué esto sí es seguro acá y
 * no en suelos.
 */
export function descartarLaPropuestaDeDescripcion(
  actual: DeslindesPorRol,
  escritos: EscritoDeDescripcion[],
): DeslindesPorRol {
  let deslindes = actual;

  for (const escrito of escritos) {
    const delRol = deslindes[escrito.id];

    if (!delRol || delRol.texto !== escrito.texto) continue;

    deslindes = { ...deslindes, [escrito.id]: { texto: "", validada: escrito.validadaAntes } };
  }

  return deslindes;
}

/**
 * Qué decir después del clic.
 *
 * EL VERBO CONCUERDA CON "LOS DESLINDES", NO CON EL NÚMERO DE ROLES: el
 * sujeto de la frase es plural aunque haya un solo rol ("Se agregaron los
 * deslindes de 1 rol."), así que el conteo solo mueve "rol"/"roles".
 */
export function resumenDeLoPropuestoEnDescripcion(
  escritos: EscritoDeDescripcion[],
  datosLegales = 0,
): string {
  const deslindes = `los deslindes de ${escritos.length} ${escritos.length === 1 ? "rol" : "roles"}`;

  if (escritos.length === 0 && datosLegales === 0) {
    return "No encontramos deslindes nuevos que agregar en los documentos leídos.";
  }

  if (datosLegales === 0) return `Se agregaron ${deslindes}.`;

  if (escritos.length === 0) {
    return datosLegales === 1 ? "Se agregó 1 dato legal." : `Se agregaron ${datosLegales} datos legales.`;
  }

  return `Se agregaron ${deslindes} y ${datosLegales} ${datosLegales === 1 ? "dato legal" : "datos legales"}.`;
}

/**
 * Cómo se resuelve la corrida al GUARDAR la pestaña.
 *
 * ACEPTADA si al menos uno de los roles que la IA escribió conserva texto,
 * aunque la persona lo haya corregido: una corrección sobre lo propuesto
 * sigue siendo la propuesta usada como punto de partida. DESCARTADA si no
 * escribió nada o si la persona vació todo lo que escribió.
 *
 * NO BASTA CON "HAY UNA CORRIDA", que era la regla anterior: contaba como
 * aceptada una lectura que no había dejado ni una línea, y eso infla la única
 * medida de si la función vale lo que cuesta.
 */
export function resultadoAlGuardar(
  actual: DeslindesPorRol,
  escritos: EscritoDeDescripcion[],
): "aceptado" | "descartado" {
  const quedoAlgo = escritos.some((escrito) => (actual[escrito.id]?.texto.trim() ?? "") !== "");

  return quedoAlgo ? "aceptado" : "descartado";
}
